/**
 * Badge Composer — Génération de badges SVG composites
 * Challenges V2
 *
 * Combine : forme + couleur fond + icône Tabler + couleur icône + texte optionnel
 * Retourne un SVG complet en data URL
 */

// --- Formes disponibles ---

export interface BadgeShape {
  id: string;
  label: { fr: string; en: string };
  /** SVG path pour le fond (viewBox 0 0 200 200) */
  path: string;
}

export const BADGE_SHAPES: BadgeShape[] = [
  {
    id: "circle",
    label: { fr: "Cercle", en: "Circle" },
    path: '<circle cx="100" cy="100" r="95" />',
  },
  {
    id: "hexagon",
    label: { fr: "Hexagone", en: "Hexagon" },
    path: '<polygon points="100,5 190,52.5 190,147.5 100,195 10,147.5 10,52.5" />',
  },
  {
    id: "shield",
    label: { fr: "Bouclier", en: "Shield" },
    path: '<path d="M100 8 L185 45 L185 110 Q185 165 100 195 Q15 165 15 110 L15 45 Z" />',
  },
  {
    id: "star",
    label: { fr: "Étoile", en: "Star" },
    path: '<polygon points="100,10 125,75 195,80 140,125 160,192 100,155 40,192 60,125 5,80 75,75" />',
  },
  {
    id: "square-rounded",
    label: { fr: "Carré arrondi", en: "Rounded square" },
    path: '<rect x="10" y="10" width="180" height="180" rx="30" />',
  },
  {
    id: "diamond",
    label: { fr: "Losange", en: "Diamond" },
    path: '<polygon points="100,5 195,100 100,195 5,100" />',
  },
];

// --- Couleurs disponibles ---

export interface BadgeColor {
  id: string;
  hex: string;
  label: { fr: string; en: string };
}

export const BADGE_COLORS: BadgeColor[] = [
  // Charte KESTIO
  { id: "gold", hex: "#F6C847", label: { fr: "Or", en: "Gold" } },
  { id: "green", hex: "#7CBF94", label: { fr: "Vert menthe", en: "Mint" } },
  { id: "purple", hex: "#988EF2", label: { fr: "Violet", en: "Purple" } },
  { id: "coral", hex: "#F49390", label: { fr: "Corail", en: "Coral" } },
  { id: "blue", hex: "#A6D3F9", label: { fr: "Bleu ciel", en: "Sky blue" } },
  { id: "navy", hex: "#384872", label: { fr: "Bleu nuit", en: "Navy" } },
  // Métalliques
  { id: "silver", hex: "#C0C0C0", label: { fr: "Argent", en: "Silver" } },
  { id: "bronze", hex: "#CD7F32", label: { fr: "Bronze", en: "Bronze" } },
  // Élargie
  { id: "red", hex: "#E74C3C", label: { fr: "Rouge", en: "Red" } },
  { id: "orange", hex: "#F39C12", label: { fr: "Orange", en: "Orange" } },
  { id: "yellow", hex: "#F1C40F", label: { fr: "Jaune vif", en: "Bright yellow" } },
  { id: "emerald", hex: "#2ECC71", label: { fr: "Émeraude", en: "Emerald" } },
  { id: "teal", hex: "#1ABC9C", label: { fr: "Turquoise", en: "Teal" } },
  { id: "cyan", hex: "#00BCD4", label: { fr: "Cyan", en: "Cyan" } },
  { id: "indigo", hex: "#3F51B5", label: { fr: "Indigo", en: "Indigo" } },
  { id: "deep-purple", hex: "#673AB7", label: { fr: "Violet profond", en: "Deep purple" } },
  { id: "pink", hex: "#E91E63", label: { fr: "Rose vif", en: "Hot pink" } },
  { id: "brown", hex: "#795548", label: { fr: "Marron", en: "Brown" } },
  { id: "grey", hex: "#607D8B", label: { fr: "Gris bleu", en: "Blue grey" } },
  { id: "black", hex: "#2C3E50", label: { fr: "Noir", en: "Black" } },
  { id: "white", hex: "#FFFFFF", label: { fr: "Blanc", en: "White" } },
];

// --- Icônes Tabler (paths extraits, MIT license) ---

export interface BadgeIcon {
  id: string;
  label: { fr: string; en: string };
  /** SVG inner content (paths) — viewBox 0 0 24 24, stroke-width 2, stroke currentColor */
  svg: string;
}

export const BADGE_ICONS: BadgeIcon[] = [
  {
    id: "trophy",
    label: { fr: "Trophée", en: "Trophy" },
    svg: '<path d="M17 3a1 1 0 0 1 .993 .883l.007 .117v2.17a3 3 0 1 1 0 5.659v.171a6.002 6.002 0 0 1 -5 5.917v2.083h3a1 1 0 0 1 .117 1.993l-.117 .007h-8a1 1 0 0 1 -.117 -1.993l.117 -.007h3v-2.083a6.002 6.002 0 0 1 -4.996 -5.692l-.004 -.225v-.171a3 3 0 0 1 -3.996 -2.653l-.003 -.176l.005 -.176a3 3 0 0 1 3.995 -2.654l-.001 -2.17a1 1 0 0 1 1 -1h10zm-12 5a1 1 0 1 0 0 2a1 1 0 0 0 0 -2m14 0a1 1 0 1 0 0 2a1 1 0 0 0 0 -2" />',
  },
  {
    id: "medal",
    label: { fr: "Médaille", en: "Medal" },
    svg: '<path d="M12 4v3m-4 -3v6m8 -6v6" /><path d="M12 18.5l-3 1.5l.5 -3.5l-2 -2l3 -.5l1.5 -3l1.5 3l3 .5l-2 2l.5 3.5l-3 -1.5" />',
  },
  {
    id: "award",
    label: { fr: "Récompense", en: "Award" },
    svg: '<path d="M19.496 13.983l1.966 3.406a1.001 1.001 0 0 1 -.705 1.488l-.113 .011l-.112 -.001l-2.933 -.19l-1.303 2.636a1.001 1.001 0 0 1 -1.608 .26l-.082 -.094l-.072 -.11l-1.968 -3.407a8.994 8.994 0 0 0 6.93 -3.999z" /><path d="M11.43 17.982l-1.966 3.408a1.001 1.001 0 0 1 -1.622 .157l-.076 -.1l-.064 -.114l-1.304 -2.635l-2.931 .19a1.001 1.001 0 0 1 -1.022 -1.29l.04 -.107l.05 -.1l1.968 -3.409a8.994 8.994 0 0 0 6.927 4.001z" /><path d="M12 2l.24 .004a7 7 0 0 1 6.76 6.996l-.003 .193l-.007 .192l-.018 .245l-.026 .242l-.024 .178a6.985 6.985 0 0 1 -.317 1.268l-.116 .308l-.153 .348a7.001 7.001 0 0 1 -12.688 -.028l-.13 -.297l-.052 -.133l-.08 -.217l-.095 -.294a6.96 6.96 0 0 1 -.093 -.344l-.06 -.271l-.049 -.271l-.02 -.139l-.039 -.323l-.024 -.365l-.006 -.292a7 7 0 0 1 6.76 -6.996l.24 -.004z" />',
  },
  {
    id: "star",
    label: { fr: "Étoile", en: "Star" },
    svg: '<path d="M8.243 7.34l-6.38 .925l-.113 .023a1 1 0 0 0 -.44 1.684l4.622 4.499l-1.09 6.355l-.013 .11a1 1 0 0 0 1.464 .944l5.706 -3l5.693 3l.1 .046a1 1 0 0 0 1.352 -1.1l-1.091 -6.355l4.624 -4.5l.078 -.085a1 1 0 0 0 -.633 -1.62l-6.38 -.926l-2.852 -5.78a1 1 0 0 0 -1.794 0l-2.853 5.78z" />',
  },
  {
    id: "crown",
    label: { fr: "Couronne", en: "Crown" },
    svg: '<path d="M19 19h-14c-.5 0 -.9 -.3 -1 -.8l-2 -10c0 -.4 .1 -.8 .5 -1.1c.4 -.2 .8 -.2 1.1 0l4.1 3.3l3.4 -5.1c.4 -.6 1.3 -.6 1.7 0l3.4 5.1l4.1 -3.3c.3 -.3 .8 -.3 1.1 0c.4 .2 .5 .6 .5 1.1l-2 10c0 .5 -.5 .8 -1 .8z" />',
  },
  {
    id: "diamond",
    label: { fr: "Diamant", en: "Diamond" },
    svg: '<path d="M18 4a1 1 0 0 1 .783 .378l.074 .108l3 5a1 1 0 0 1 -.032 1.078l-.08 .103l-8.53 9.533a1.7 1.7 0 0 1 -1.215 .51c-.4 0 -.785 -.14 -1.11 -.417l-.135 -.126l-8.5 -9.5a1 1 0 0 1 -.172 -1.067l.06 -.115l3.013 -5.022l.064 -.09a.982 .982 0 0 1 .155 -.154l.089 -.064l.088 -.05l.05 -.023l.06 -.025l.109 -.032l.112 -.02l.117 -.005h12z" />',
  },
  {
    id: "shield-check",
    label: { fr: "Bouclier", en: "Shield" },
    svg: '<path d="M11.998 2l.118 .007l.059 .008l.061 .013l.111 .034a.993 .993 0 0 1 .217 .112l.104 .082l.255 .218a11 11 0 0 0 7.189 2.537l.342 -.01a1 1 0 0 1 1.005 .717a13 13 0 0 1 -9.208 16.25a1 1 0 0 1 -.502 0a13 13 0 0 1 -9.209 -16.25a1 1 0 0 1 1.005 -.717a11 11 0 0 0 7.531 -2.527l.263 -.225l.096 -.075a.993 .993 0 0 1 .217 -.112l.112 -.034a.97 .97 0 0 1 .119 -.021l.115 -.007zm3.71 7.293a1 1 0 0 0 -1.415 0l-3.293 3.292l-1.293 -1.292l-.094 -.083a1 1 0 0 0 -1.32 1.497l2 2l.094 .083a1 1 0 0 0 1.32 -.083l4 -4l.083 -.094a1 1 0 0 0 -.083 -1.32z" />',
  },
  {
    id: "target",
    label: { fr: "Cible", en: "Target" },
    svg: '<circle cx="12" cy="12" r="1" /><circle cx="12" cy="12" r="5" fill="none" stroke="currentColor" stroke-width="2" /><circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" stroke-width="2" />',
  },
  {
    id: "flame",
    label: { fr: "Flamme", en: "Flame" },
    svg: '<path d="M10 2c0 -.88 1.056 -1.331 1.692 -.722c1.958 1.876 3.096 5.995 1.75 9.12l-.08 .174l.012 .003c.625 .133 1.203 -.43 2.303 -2.173l.14 -.224a1 1 0 0 1 1.582 -.153c1.334 1.435 2.601 4.377 2.601 6.27c0 4.265 -3.591 7.705 -8 7.705s-8 -3.44 -8 -7.706c0 -2.252 1.022 -4.716 2.632 -6.301l.605 -.589c.241 -.236 .434 -.43 .618 -.624c1.43 -1.512 2.145 -2.924 2.145 -4.78" />',
  },
  {
    id: "bolt",
    label: { fr: "Éclair", en: "Lightning" },
    svg: '<path d="M13 2l.018 .001l.016 .001l.083 .005l.011 .002h.011l.038 .009l.052 .008l.016 .006l.011 .001l.029 .011l.052 .014l.019 .009l.015 .004l.028 .014l.04 .017l.021 .012l.022 .01l.023 .015l.031 .017l.034 .024l.018 .011l.013 .012l.024 .017l.038 .034l.022 .017l.008 .01l.014 .012l.036 .041l.026 .027l.006 .009c.12 .147 .196 .322 .218 .513l.001 .012l.002 .041l.004 .064v6h5a1 1 0 0 1 .868 1.497l-.06 .091l-8 11c-.568 .783 -1.808 .38 -1.808 -.588v-6h-5a1 1 0 0 1 -.868 -1.497l.06 -.091l8 -11z" />',
  },
  {
    id: "rocket",
    label: { fr: "Fusée", en: "Rocket" },
    svg: '<path d="M4 13a8 8 0 0 1 7 7a6 6 0 0 0 3 -5a9 9 0 0 0 6 -8a3 3 0 0 0 -3 -3a9 9 0 0 0 -8 6a6 6 0 0 0 -5 3" /><path d="M7 14a6 6 0 0 0 -3 6a6 6 0 0 0 6 -3" /><circle cx="15" cy="9" r="1" />',
  },
  {
    id: "heart",
    label: { fr: "Cœur", en: "Heart" },
    svg: '<path d="M6.979 3.074a6 6 0 0 1 4.988 1.425l.037 .033l.034 -.03a6 6 0 0 1 4.733 -1.44l.246 .036a6 6 0 0 1 3.364 10.008l-.18 .185l-.048 .041l-7.45 7.379a1 1 0 0 1 -1.313 .082l-.094 -.082l-7.493 -7.422a6 6 0 0 1 3.176 -10.215z" />',
  },
  {
    id: "certificate",
    label: { fr: "Certificat", en: "Certificate" },
    svg: '<circle cx="15" cy="15" r="3" fill="none" stroke="currentColor" stroke-width="2" /><path d="M13 17.5v4.5l2 -1.5l2 1.5v-4.5" /><path d="M10 19h-5a2 2 0 0 1 -2 -2v-10c0 -1.1 .9 -2 2 -2h14a2 2 0 0 1 2 2v10a2 2 0 0 1 -1 1.73" /><path d="M6 9l12 0" /><path d="M6 12l3 0" /><path d="M6 15l2 0" />',
  },
  {
    id: "gift",
    label: { fr: "Cadeau", en: "Gift" },
    svg: '<path d="M11 14v8h-4a3 3 0 0 1 -3 -3v-4a1 1 0 0 1 1 -1h6zm8 0a1 1 0 0 1 1 1v4a3 3 0 0 1 -3 3h-4v-8h6zm-2.5 -12a3.5 3.5 0 0 1 3.163 5h.337a2 2 0 0 1 2 2v1a2 2 0 0 1 -2 2h-7v-5h-2v5h-7a2 2 0 0 1 -2 -2v-1a2 2 0 0 1 2 -2h.337a3.486 3.486 0 0 1 -.337 -1.5c0 -1.933 1.567 -3.5 3.483 -3.5c1.755 -.03 3.312 1.092 4.381 2.934l.136 .243c1.033 -1.914 2.56 -3.114 4.291 -3.175l.209 -.002z" />',
  },
  {
    id: "sparkles",
    label: { fr: "Étincelles", en: "Sparkles" },
    svg: '<path d="M16 18a2 2 0 0 1 2 2a2 2 0 0 1 2 -2a2 2 0 0 1 -2 -2a2 2 0 0 1 -2 2m0 -12a2 2 0 0 1 2 2a2 2 0 0 1 2 -2a2 2 0 0 1 -2 -2a2 2 0 0 1 -2 2m-7 12a6 6 0 0 1 6 -6a6 6 0 0 1 -6 -6a6 6 0 0 1 -6 6a6 6 0 0 1 6 6" />',
  },
  {
    id: "thumb-up",
    label: { fr: "Pouce levé", en: "Thumbs up" },
    svg: '<path d="M13 3a3 3 0 0 1 2.995 2.824l.005 .176v4h2a3 3 0 0 1 2.98 2.65l.015 .174l.005 .176l-.02 .196l-1.006 5.032c-.381 1.626 -1.502 2.796 -2.81 2.78l-.164 -.008h-8a1 1 0 0 1 -.993 -.883l-.007 -.117l.001 -9.536a1 1 0 0 1 .5 -.865a2.998 2.998 0 0 0 1.492 -2.397l.007 -.202v-1a3 3 0 0 1 3 -3z" /><path d="M5 10a1 1 0 0 1 .993 .883l.007 .117v9a1 1 0 0 1 -.883 .993l-.117 .007h-1a2 2 0 0 1 -1.995 -1.85l-.005 -.15v-7a2 2 0 0 1 1.85 -1.995l.15 -.005h1z" />',
  },
  {
    id: "rosette",
    label: { fr: "Rosette", en: "Rosette" },
    svg: '<path d="M12.01 2.011c.852 0 1.668 .34 2.267 .942l.698 .698a1.2 1.2 0 0 0 .845 .349h1a3.2 3.2 0 0 1 3.2 3.2v1c0 .316 .126 .62 .347 .843l.698 .698a3.2 3.2 0 0 1 .002 4.536l-.698 .698a1.2 1.2 0 0 0 -.349 .845v1a3.2 3.2 0 0 1 -3.2 3.2h-1a1.2 1.2 0 0 0 -.843 .347l-.698 .698a3.2 3.2 0 0 1 -4.536 .002l-.698 -.698a1.2 1.2 0 0 0 -.845 -.349h-1a3.2 3.2 0 0 1 -3.2 -3.2v-1a1.2 1.2 0 0 0 -.347 -.843l-.698 -.698a3.2 3.2 0 0 1 -.002 -4.536l.698 -.698a1.2 1.2 0 0 0 .349 -.845v-1l.005 -.182a3.2 3.2 0 0 1 3.195 -3.018h1a1.2 1.2 0 0 0 .843 -.347l.698 -.698a3.2 3.2 0 0 1 2.269 -.944z" />',
  },
  {
    id: "school",
    label: { fr: "Diplôme", en: "Graduation" },
    svg: '<path d="M22 9l-10 -4l-10 4l10 4l10 -4v6" /><path d="M6 10.6v5.4a6 3 0 0 0 12 0v-5.4" />',
  },
  {
    id: "world",
    label: { fr: "Globe", en: "Globe" },
    svg: '<circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" stroke-width="2" /><path d="M3.6 9h16.8" /><path d="M3.6 15h16.8" /><path d="M11.5 3a17 17 0 0 0 0 18" /><path d="M12.5 3a17 17 0 0 1 0 18" />',
  },
  {
    id: "chart-line",
    label: { fr: "Progression", en: "Progress" },
    svg: '<path d="M4 19l16 0" /><path d="M4 15l4 -6l4 2l4 -5l4 4" />',
  },
  {
    id: "circle-check",
    label: { fr: "Validation", en: "Check" },
    svg: '<path d="M17 3.34a10 10 0 1 1 -14.995 8.984l-.005 -.324l.005 -.324a10 10 0 0 1 14.995 -8.336zm-1.293 5.953a1 1 0 0 0 -1.32 -.083l-.094 .083l-3.293 3.292l-1.293 -1.292l-.094 -.083a1 1 0 0 0 -1.403 1.403l.083 .094l2 2l.094 .083a1 1 0 0 0 1.226 0l.094 -.083l4 -4l.083 -.094a1 1 0 0 0 -.083 -1.32z" />',
  },
  {
    id: "sword",
    label: { fr: "Épée", en: "Sword" },
    svg: '<path d="M20 4v5l-9 7l-4 4l-3 -3l4 -4l7 -9l5 0" /><path d="M6.5 11.5l6 6" />',
  },
  {
    id: "bulb",
    label: { fr: "Ampoule", en: "Lightbulb" },
    svg: '<path d="M14 18a1 1 0 0 1 1 1a3 3 0 0 1 -6 0a1 1 0 0 1 .883 -.993l.117 -.007h4z" /><path d="M12 6a6 6 0 0 1 3.6 10.8a1 1 0 0 1 -.471 .192l-.129 .008h-6a1 1 0 0 1 -.6 -.2a6 6 0 0 1 3.6 -10.8z" />',
  },
  {
    id: "puzzle",
    label: { fr: "Puzzle", en: "Puzzle" },
    svg: '<path d="M10 2a3 3 0 0 1 2.995 2.824l.005 .176v1h3a2 2 0 0 1 1.995 1.85l.005 .15v3h1a3 3 0 0 1 .176 5.995l-.176 .005h-1v3a2 2 0 0 1 -1.85 1.995l-.15 .005h-3a2 2 0 0 1 -1.995 -1.85l-.005 -.15v-1a1 1 0 0 0 -1.993 -.117l-.007 .117v1a2 2 0 0 1 -1.85 1.995l-.15 .005h-3a2 2 0 0 1 -1.995 -1.85l-.005 -.15v-3a2 2 0 0 1 1.85 -1.995l.15 -.005h1a1 1 0 0 0 .117 -1.993l-.117 -.007h-1a2 2 0 0 1 -1.995 -1.85l-.005 -.15v-3a2 2 0 0 1 1.85 -1.995l.15 -.005h3v-1a3 3 0 0 1 3 -3z" />',
  },
  {
    id: "flag",
    label: { fr: "Drapeau", en: "Flag" },
    svg: '<path d="M19 4c.852 0 1.297 .986 .783 1.623l-.076 .084l-3.792 3.793l3.792 3.793c.603 .602 .22 1.614 -.593 1.701l-.114 .006h-13v6a1 1 0 0 1 -.883 .993l-.117 .007a1 1 0 0 1 -.993 -.883l-.007 -.117v-16a1 1 0 0 1 .883 -.993l.117 -.007h14z" />',
  },
  {
    id: "users",
    label: { fr: "Équipe", en: "Team" },
    svg: '<circle cx="12" cy="13" r="2" fill="none" stroke="currentColor" stroke-width="2" /><path d="M8 21v-1a2 2 0 0 1 2 -2h4a2 2 0 0 1 2 2v1" /><circle cx="17" cy="5" r="2" fill="none" stroke="currentColor" stroke-width="2" /><path d="M17 10h2a2 2 0 0 1 2 2v1" /><circle cx="7" cy="5" r="2" fill="none" stroke="currentColor" stroke-width="2" /><path d="M3 13v-1a2 2 0 0 1 2 -2h2" />',
  },
  {
    id: "briefcase",
    label: { fr: "Business", en: "Business" },
    svg: '<path d="M14 2a3 3 0 0 1 3 3v1h2a3 3 0 0 1 3 3v9a3 3 0 0 1 -3 3h-14a3 3 0 0 1 -3 -3v-9a3 3 0 0 1 3 -3h2v-1a3 3 0 0 1 3 -3zm0 2h-4a1 1 0 0 0 -1 1v1h6v-1a1 1 0 0 0 -1 -1" />',
  },
  {
    id: "message",
    label: { fr: "Communication", en: "Communication" },
    svg: '<path d="M5.821 4.91c3.899 -2.765 9.468 -2.539 13.073 .535c3.667 3.129 4.168 8.238 1.152 11.898c-2.841 3.447 -7.965 4.583 -12.231 2.805l-.233 -.101l-4.374 .931l-.04 .006l-.035 .007h-.018l-.022 .005h-.038l-.033 .004l-.021 -.001l-.023 .001l-.033 -.003h-.035l-.022 -.004l-.022 -.002l-.035 -.007l-.034 -.005l-.016 -.004l-.024 -.005l-.049 -.016l-.024 -.005l-.011 -.005l-.022 -.007l-.045 -.02l-.03 -.012l-.011 -.006l-.014 -.006l-.031 -.018l-.045 -.024l-.016 -.011l-.037 -.026l-.04 -.027l-.002 -.004l-.013 -.009l-.043 -.04l-.025 -.02l-.006 -.007l-.056 -.062l-.013 -.014l-.011 -.014l-.039 -.056l-.014 -.019l-.005 -.01l-.042 -.073l-.007 -.012l-.004 -.008l-.007 -.012l-.014 -.038l-.02 -.042l-.004 -.016l-.004 -.01l-.017 -.061l-.007 -.018l-.002 -.015l-.005 -.019l-.005 -.033l-.008 -.042l-.002 -.031l-.003 -.01v-.016l-.004 -.054l.001 -.036l.001 -.023l.002 -.053l.004 -.025v-.019l.008 -.035l.005 -.034l.005 -.02l.004 -.02l.018 -.06l.003 -.013l1.15 -3.45l-.022 -.037c-2.21 -3.747 -1.209 -8.391 2.413 -11.119z" />',
  },
  {
    id: "clock",
    label: { fr: "Chrono", en: "Timer" },
    svg: '<path d="M17 3.34a10 10 0 1 1 -15 8.66l.005 -.324a10 10 0 0 1 14.995 -8.336m-5 2.66a1 1 0 0 0 -1 1v5a1 1 0 0 0 1 1h3.5a1 1 0 0 0 0 -2h-2.5v-4a1 1 0 0 0 -.883 -.993z" />',
  },
  {
    id: "brain",
    label: { fr: "Cerveau", en: "Brain" },
    svg: '<path d="M15.5 13a3.5 3.5 0 0 0 -3.5 3.5v1a3.5 3.5 0 0 0 7 0v-1.8" /><path d="M8.5 13a3.5 3.5 0 0 1 3.5 3.5v1a3.5 3.5 0 0 1 -7 0v-1.8" /><path d="M17.5 16a3.5 3.5 0 0 0 0 -7h-.5" /><path d="M19 9.3v-2.8a3.5 3.5 0 0 0 -7 0" /><path d="M6.5 16a3.5 3.5 0 0 1 0 -7h.5" /><path d="M5 9.3v-2.8a3.5 3.5 0 0 1 7 0v10" />',
  },
  {
    id: "infinity",
    label: { fr: "Infini", en: "Infinity" },
    svg: '<path d="M9.828 9.172a4 4 0 1 0 0 5.656a10 10 0 0 0 2.172 -2.828a10 10 0 0 1 2.172 -2.828a4 4 0 1 1 0 5.656a10 10 0 0 1 -2.172 -2.828a10 10 0 0 0 -2.172 -2.828" />',
  },
  {
    id: "phone",
    label: { fr: "Appel", en: "Call" },
    svg: '<path d="M9.928 3.629l2 5a1 1 0 0 1 -.414 1.228l-1.67 1.003a9.9 9.9 0 0 0 3.296 3.297l1.003 -1.671a1 1 0 0 1 1.228 -.414l5 2a1 1 0 0 1 .629 .928v4c0 1.657 -1.343 3 -3.06 2.998c-8.579 -.521 -15.418 -7.36 -15.94 -15.998a3 3 0 0 1 3 -3h4a1 1 0 0 1 .928 .629" />',
  },
  {
    id: "cash",
    label: { fr: "Business", en: "Money" },
    svg: '<path d="M7 15h-3a1 1 0 0 1 -1 -1v-8a1 1 0 0 1 1 -1h12a1 1 0 0 1 1 1v3" /><rect x="7" y="9" width="14" height="10" rx="1" fill="none" stroke="currentColor" stroke-width="2" /><circle cx="14" cy="14" r="2" fill="none" stroke="currentColor" stroke-width="2" />',
  },
  {
    id: "microphone",
    label: { fr: "Micro", en: "Mic" },
    svg: '<path d="M15 12.9a5 5 0 1 0 -3.902 -3.9" /><path d="M15 12.9l-3.902 -3.899l-7.513 8.584a2 2 0 1 0 2.827 2.83l8.588 -7.515" />',
  },
  {
    id: "medal-2",
    label: { fr: "Médaille 2", en: "Medal 2" },
    svg: '<path d="M9 3h6l3 7l-6 2l-6 -2l3 -7" /><path d="M12 12l-3 -9" /><path d="M15 11l-3 -8" /><path d="M12 19.5l-3 1.5l.5 -3.5l-2 -2l3 -.5l1.5 -3l1.5 3l3 .5l-2 2l.5 3.5l-3 -1.5" />',
  },
  {
    id: "plant",
    label: { fr: "Croissance", en: "Growth" },
    svg: '<circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" stroke-width="2" /><path d="M12 19a10 10 0 0 1 10 -10" /><path d="M2 9a10 10 0 0 1 10 10" /><path d="M12 4a9.7 9.7 0 0 1 2.99 7.5" /><path d="M9.01 11.5a9.7 9.7 0 0 1 2.99 -7.5" />',
  },
];

// --- Couleurs d'icône ---

/** Couleurs d'icône — même palette élargie que les fonds */
export const ICON_COLORS = BADGE_COLORS;

// --- Options texte ---

export const TEXT_SIZES = [
  { id: "small", value: 14, label: { fr: "Petit", en: "Small" } },
  { id: "medium", value: 20, label: { fr: "Moyen", en: "Medium" } },
  { id: "large", value: 28, label: { fr: "Grand", en: "Large" } },
];

export const TEXT_POSITIONS = [
  { id: "top", y: 30, label: { fr: "Haut", en: "Top" } },
  { id: "center", y: 108, label: { fr: "Centre", en: "Center" } },
  { id: "bottom", y: 185, label: { fr: "Bas", en: "Bottom" } },
];

export const TEXT_FONTS = [
  { id: "sora", value: "Sora, sans-serif", label: { fr: "Sora (titres)", en: "Sora (headings)" } },
  { id: "inter", value: "Inter, sans-serif", label: { fr: "Inter (corps)", en: "Inter (body)" } },
];

// --- Générateur SVG ---

export interface BadgeComposition {
  shapeId: string;
  bgColor: string;
  iconId: string;
  iconColor: string;
  text?: string;
  textColor?: string;
  textSize?: number;
  textPosition?: string;
  textFont?: string;
}

/**
 * Génère un SVG complet pour un badge composé.
 * Retourne la chaîne SVG brute.
 */
export function composeBadgeSvg(comp: BadgeComposition): string {
  const shape = BADGE_SHAPES.find((s) => s.id === comp.shapeId) || BADGE_SHAPES[0];
  const icon = BADGE_ICONS.find((i) => i.id === comp.iconId);

  // Texte optionnel
  const fontSize = comp.textSize || 20;
  const textPos = TEXT_POSITIONS.find((p) => p.id === comp.textPosition) || TEXT_POSITIONS[2];
  const fontFamily = comp.textFont || "Sora, sans-serif";
  const textElement = comp.text
    ? `<text x="100" y="${textPos.y}" text-anchor="middle" font-family="${fontFamily}" font-size="${fontSize}" font-weight="bold" fill="${comp.textColor || '#FFFFFF'}">${escapeXml(comp.text)}</text>`
    : "";

  // Ajuster la position de l'icône si texte en position centre
  const hasTextCenter = comp.text && comp.textPosition === "center";
  const hasTextBottom = comp.text && (!comp.textPosition || comp.textPosition === "bottom");
  const iconY = hasTextCenter ? 55 : hasTextBottom ? 60 : comp.text ? 75 : 88;
  const iconScale = comp.text ? 4.2 : 5;
  const iconOffset = (200 - 24 * iconScale) / 2;

  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200" width="200" height="200">
  <!-- Fond -->
  <g fill="${comp.bgColor}" stroke="none">${shape.path}</g>
  <!-- Icône -->
  ${icon ? `<g transform="translate(${iconOffset}, ${iconY}) scale(${iconScale})" fill="${comp.iconColor}" stroke="none">
    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
    ${icon.svg}
  </g>` : ""}
  <!-- Texte -->
  ${textElement}
</svg>`;

  return svg;
}

/**
 * Génère un data URL (base64) pour un badge composé.
 */
export function composeBadgeDataUrl(comp: BadgeComposition): string {
  const svg = composeBadgeSvg(comp);
  return `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svg)))}`;
}

function escapeXml(str: string): string {
  return str.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}
